<script lang="ts">
  import {
    calculateCreditLine,
    getRightsMarker,
    isPersonInImage,
    isPhotographer,
    isUploader,
    type Contributor,
    type Image,
  } from "bildebanken-model";
  import RightsIndicator from "components/Common/RightsIndicator.svelte";
  import CopyWrapper from "../Common/CopyWrapper.svelte";
  import TechnicalMetadata from "./TechnicalMetadata.svelte";

  import { first } from "lodash-es";
  import { bytesToSize } from "../../utils/fns";
  import type { ImageSource } from "state/page";
  import Timestamp from "components/Common/Timestamp.svelte";
  import ActionButtons from "./ActionButtons.svelte";
  import EditAltText from "components/Editor/EditAltText.svelte";
  import { getRequiredFields } from "services/plugin-customization";
  import { isPlugin } from "state/app";

  export let image: Image;
  export let itemId: string;

  export let itemSource: ImageSource;
  export let altText: string = "";
  export let onAltTextChange: ((text: string) => void) | undefined = undefined;

  const contactTitle = (contributor: Contributor) => contributor.contact.title;
  const rootStyle = `--inactive-color: #949494;`; // Contrat ratio 4:52 to (--var-org-color-gray-700)

  $: photographers = image.metadata.contributors?.filter(isPhotographer);
  $: photographerName = photographers?.length && contactTitle(first(photographers)!);
  $: personsInImage = image.metadata.contributors?.filter(isPersonInImage) || [];
  $: uploader = image.metadata.contributors?.filter(isUploader)?.pop();
  $: createdOn = new Date(image.metadata.createdOn!);

  export let externalImage = false;

  const requiredFields = getRequiredFields();

  const altTextRequired = requiredFields && requiredFields.includes("altText");

  let newAltText = "";
  let previousImageId: string | undefined;

  $: if (image?.id !== previousImageId) {
    previousImageId = image?.id;
    newAltText = "";
  }

  $: creditLine = calculateCreditLine(image.metadata);
  $: mediaCreatedOn = image.metadata.mediaCreatedOn
    ? new Date(image.metadata.mediaCreatedOn)
    : undefined;
  $: rights = getRightsMarker(image);
  $: rightsDescription =
    rights === "Free" ? "Fri bruk i NRK" : image.metadata.rights?.rightsHolder?.contact?.title;

  $: originalFile = image.originalFileName?.replace(`_${image.id}`, "");

  const technical = image.technicalMetadata;
  $: dimensions = technical?.technical_image_width &&
    technical?.technical_image_height && {
      width: technical?.technical_image_width,
      height: technical?.technical_image_height,
    };

  const today = new Date();
  const tomorrow = today.setDate(today.getDate() + 1);
  $: futureMediaCreatedOnDate =
    image.metadata.mediaCreatedOn && new Date(image.metadata.mediaCreatedOn) > new Date(tomorrow);

  $: sortedPublications =
    image.metadata.publications && image.metadata.publications?.length > 0
      ? [...image.metadata.publications].sort((a, b) =>
          (a.publishedAt ?? "").localeCompare(b.publishedAt ?? ""),
        )
      : [];
</script>

<div class="wrapper" style={rootStyle}>
  <dl>
    <dt>Tittel</dt>
    <dd>{image.metadata.title || ""}</dd>
    <dt>Beskrivelse</dt>
    <dd>{image.metadata.description || ""}</dd>
    <dt>Alternativ tekst</dt>
    {#if isPlugin && altTextRequired && (!image.metadata.altText?.trim() || newAltText)}
      <dd>
        <div>
          <EditAltText
            {altTextRequired}
            altText={newAltText || altText}
            editingSingleImage={true}
            getAltTextSuggestionOnDemand={true}
            detailMode={true}
            altTextUpdate={(text) => {
              newAltText = text;
              image.metadata.altText = text;
              onAltTextChange?.(text);
            }}
            {image}
          />
        </div>
      </dd>
    {:else}
      <dd>{image.metadata.altText || ""}</dd>
    {/if}
    <dt>Kreditering</dt>
    <dd class="credit">
      <CopyWrapper>{creditLine || ""}</CopyWrapper>
    </dd>
    <dt>Bruksvilkår</dt>
    <dd>
      <RightsIndicator rightsMarker={image.metadata.rightsMarker ?? "Unknown"} />
      {!image.metadata.rights
        ? "Ukjent"
        : [rightsDescription, image.metadata.rights?.note].filter(Boolean).join(" • ")}
    </dd>
    {#if image.metadata.instructions}
      <dt>Instruksjoner</dt>
      <dd>{image.metadata.instructions}</dd>
    {/if}
    {#if mediaCreatedOn}
      <dt>Fotografert</dt>
      <dd>
        <Timestamp timestamp={mediaCreatedOn} label="Fotografert" />
      </dd>
      {#if futureMediaCreatedOnDate && itemSource !== "Ntb"}
        <span class="org-warning">Fremtidig fotografert dato</span>
      {/if}
    {/if}
  </dl>
  <ActionButtons {itemId} {itemSource} />
  {#if !externalImage}
    <button class="org-button expand">Detaljer</button>
    <bb-expand hidden>
      <TechnicalMetadata {image} />
    </bb-expand>
  {:else if externalImage && (image.metadata.keywords || (photographerName && !creditLine?.includes(photographerName)))}
    <button class="org-button expand">Detaljer</button>
    <bb-expand hidden>
      <dl>
        {#if photographerName && !creditLine?.includes(photographerName)}
          <dt>Fotograf</dt>
          <dd>
            {image.metadata.contributors?.filter(isPhotographer)?.map(contactTitle)}
          </dd>
        {/if}
        {#if image.metadata.keywords}
          <dt>Tagger</dt>
          <dd>
            {image.metadata.keywords?.join(", ")}
          </dd>
        {/if}
      </dl>
    </bb-expand>
  {/if}
  <button class="org-button expand">Referanser</button>
  <bb-expand hidden>
    <dl>
      {#if !externalImage}
        <dt>Publiseringer</dt>
        <dd style="overflow: visible">
          {#if image.metadata.publications?.length}
            {#each sortedPublications as sortedPublication (sortedPublication.id)}
              <div class="publication-row" data-org-tooltip={sortedPublication.title}>
                <span style="width:10rem">
                  {new Date(sortedPublication.publishedAt).toLocaleDateString("no-NB")}
                </span>
                {#if sortedPublication.url}
                  <a class="publication-title" href={sortedPublication.url} target="_blank">
                    {sortedPublication.publishingTarget + ": " + sortedPublication.title}
                  </a>
                {:else}
                  <span class="publication-title">
                    {sortedPublication.publishingTarget + ": " + sortedPublication.title}
                  </span>
                {/if}
              </div>
            {/each}
          {:else}
            <span class="inactive">Ingen</span>
          {/if}
        </dd>
      {/if}
      <dt>Original</dt>
      <dd class="flex original">
        {#if dimensions}
          <span title="Dimensjoner">
            {dimensions.width} × {dimensions.height}
          </span>
        {/if}
        {#if image.mediaType}
          <span class="tag" title="Filformat">
            {image.mediaType.split("/")?.pop()?.toUpperCase()}
          </span>
        {/if}
        {#if image.mediaSize}<span title="Filstørrelse">{bytesToSize(image.mediaSize)}</span>{/if}
        <span class="filename" title="Filnavn"> {originalFile}</span>
      </dd>
      <dt>Opprettet</dt>
      <dd class="flex">
        {#if uploader || image.metadata.sourceSystem}
          <span>
            {#if uploader}
              <span title="Opplaster">{uploader.contact.title}</span>
            {/if}
            {#if image.metadata.sourceSystem}
              (<span title="Kilde">{image.metadata.sourceSystem}</span>)
            {/if}
          </span>
        {/if}
        {#if createdOn}
          <span title={createdOn.toLocaleTimeString()}>
            {createdOn.toLocaleDateString("no-NB")}
          </span>
        {/if}
      </dd>
      {#if itemSource === "Ntb"}
        <dt>NTB-ID</dt>
        <dd>
          <CopyWrapper title="Kopier NTB-ID">
            <span class="monospace">{image.id}</span>
          </CopyWrapper>
        </dd>
      {:else if itemSource === "Bildebanken"}
        <dt>Bildebanken-ID</dt>
        <dd>
          <CopyWrapper title="Kopier Bildebanken-ID">
            <span class="monospace">{image.id}</span>
          </CopyWrapper>
        </dd>
      {/if}
      {#if image.metadata.jobId}
        <dt>Oppdrag-ID</dt>
        <dd>
          <CopyWrapper text={image.metadata.jobId} title="Kopier Oppdrags-ID">
            <span class="monospace">{image.metadata.jobId}</span>
          </CopyWrapper>
        </dd>
      {/if}
      {#if image.metadata.publicId}
        <dt>Kaleido-ID</dt>
        <dd>
          <CopyWrapper title="Kopier Kaleido-ID">
            <span class="monospace">{image.metadata.publicId}</span>
          </CopyWrapper>
        </dd>
      {/if}
    </dl>
  </bb-expand>
</div>

<style>
  .wrapper {
    display: flex;
    flex-direction: column;
  }

  .monospace {
    font-family: monospace;
    font-size: 0.8rem;
  }

  .inactive {
    color: var(--inactive-color);
  }

  :global(dd.flex) {
    display: flex;
    gap: 1.5em;
  }

  .flex.original {
    flex-wrap: wrap;
  }

  .original > :not(:last-child) {
    white-space: nowrap;
  }

  bb-expand {
    margin-top: 0.5em;
  }

  .org-button.expand {
    padding-left: 0;
  }

  .publication-row {
    display: grid;
    grid-template-columns: 90px 150fr;
  }

  .publication-title {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  a.publication-title {
    color: var(--org-color-focus);
  }

  button.expand {
    text-align: left;
    margin-top: var(--org-medium);
  }

  a {
    color: unset;
    text-decoration: none;
  }

  .tag {
    background-color: var(--org-color-gray-500);
    color: var(--org-color-gray-900);
    padding: 2px 4px;
    border-radius: 5px;
    font-size: 12px;
  }

  .filename {
    opacity: 0;
    transition: 0.25s;
    color: var(--org-color-gray-400);
  }

  dd:hover .filename {
    opacity: 1;
  }

  :global(dl) {
    display: grid;
    grid-template-columns: 120px 1fr;
  }

  :global(dt) {
    font-weight: 700;
  }

  :global(dt),
  :global(dd) {
    border-bottom: solid 1px var(--org-color-shade-2);
    margin: 0;
    padding: var(--org-small) 0;
    line-height: 1.5;
    overflow: hidden;
  }
</style>
